import React from "react";
import { Box, Button, Flex, Heading, HStack, Image, Text } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { FiArrowLeft, FiHome } from "react-icons/fi";
import ReducedTopbar from "../../../components/utils/ReducedTopbar";

const MotionBox = motion.create(Box);
const MotionImage = motion.create(Image);

export default function StatusPage({
  image,
  imageAlt,
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  showLogout = false,
  showTopbar = true,
}) {
  const reduceMotion = useReducedMotion();

  return <Flex
    minH="100dvh" position="relative" overflow="hidden"
    px={{ base: "6", lg: "12" }} py={{ base: "24", lg: "12" }}
    bg="public.bg.canvas" color="public.fg.default"
    align="center" justify="center"
  >
    <Box position="absolute" boxSize={{ base: "220px", lg: "360px" }} rounded="full" bg="#dce8f1" _dark={{ bg: "#243746" }} right={{ base: "-130px", lg: "-100px" }} top={{ base: "12%", lg: "-100px" }} opacity=".72"/>
    <Box position="absolute" w={{ base: "230px", lg: "380px" }} h={{ base: "100px", lg: "170px" }} rounded="full" bg="#eadde8" _dark={{ bg: "#3d3040" }} left={{ base: "-130px", lg: "35%" }} bottom={{ base: "4%", lg: "-100px" }} transform="rotate(-16deg)" opacity=".66"/>
    {showTopbar && <ReducedTopbar showLogout={showLogout}/>}

    <MotionBox
      role="status" aria-live="polite" position="relative" zIndex="1"
      w="full" maxW={{ base: "520px", lg: "960px" }}
      display={{ lg: "grid" }} gridTemplateColumns={{ lg: "minmax(0, 1fr) minmax(320px, .9fr)" }}
      alignItems="center" gap={{ lg: "12" }} textAlign={{ base: "center", lg: "left" }}
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <Box h={{ base: "280px", md: "330px", lg: "320px" }} position="relative">
        <MotionBox
          position="absolute" inset="5% 8%" rounded="full"
          bg="linear-gradient(145deg, #dce8f1, #eadde8)"
          _dark={{ bg: "linear-gradient(145deg, #243746, #3d3040)" }}
          boxShadow="0 24px 60px rgba(64,74,96,.18)"
          animate={reduceMotion ? undefined : { scale: [1, 1.04, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <MotionImage
          src={image} alt={imageAlt} position="relative"
          h="full" w="full" objectFit="contain"
          filter="drop-shadow(0 22px 28px rgba(127,29,29,.14))"
          animate={reduceMotion ? undefined : { y: [0, -7, 0], rotate: [0, 1, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </Box>

      <Box>
        <Text mt={{ base: "5", lg: "0" }} color="brand.red" fontSize="sm" fontWeight="800" letterSpacing="wide" textTransform="uppercase">{eyebrow}</Text>
        <Heading mt="2" fontSize={{ base: "32px", md: "42px" }} lineHeight="1.08">{title}</Heading>
        <Text mt="4" mx={{ base: "auto", lg: "0" }} maxW="460px" color="public.fg.muted" lineHeight="1.7">{description}</Text>
        {children}

        <HStack mt="8" gap="3" justify={{ base: "center", lg: "flex-start" }} flexWrap="wrap">
          {primaryAction && <Button h="48px" px="7" rounded="full" bg="brand.red" color="gray.50" fontWeight="800" _hover={{ bg: "brand.redHover" }} onClick={primaryAction.onClick}>
            {primaryAction.icon || <FiHome/>}{primaryAction.label}
          </Button>}
          {secondaryAction && <Button h="48px" px="7" rounded="full" variant="ghost" color="public.fg.muted" fontWeight="800" _hover={{ bg: "public.bg.muted", color: "public.fg.default" }} onClick={secondaryAction.onClick}>
            {secondaryAction.icon || <FiArrowLeft/>}{secondaryAction.label}
          </Button>}
        </HStack>
      </Box>
    </MotionBox>
  </Flex>;
}
